import React, {
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { SetType } from '@/components/Template/types';

export type SetEditorFlow =
  | 'createSet'
  | 'editSet'
  | 'createTemplate'
  | 'editTemplate'
  | 'templateFromSet'
  | 'duplicateTemplate';

export function isTemplateFlow(flow: SetEditorFlow) {
  return (
    flow === 'createTemplate' ||
    flow === 'editTemplate' ||
    flow === 'templateFromSet' ||
    flow === 'duplicateTemplate'
  );
}

export interface SetEditorFormData {
  type: SetType | null;
  name: string;
  appDisplayName: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  description?: string;
}

interface SetEditorContent {
  apiKeyName: 'wordsets' | 'sentencesets' | 'textsets';
  items: unknown[];
  dataKeyName: string;
  dynamicName: string;
  patientId?: string;
  setId?: string;
  editData?: Record<string, unknown> | null;
  fromTemplate?: string;
}

interface SetEditorContextType {
  formData: SetEditorFormData;
  savePayload: SetEditorContent | null;
  setFormData: (data: Partial<SetEditorFormData>) => void;
  setSavePayload: (payload: SetEditorContent | null) => void;
  resetFormData: () => void;
}

export const SetEditorContext = createContext<SetEditorContextType | undefined>(
  undefined,
);

const defaultFormData: SetEditorFormData = {
  type: null,
  name: '',
  appDisplayName: '',
};

interface SetEditorContextProviderProps {
  children: ReactNode;
}

export function SetEditorContextProvider({
  children,
}: SetEditorContextProviderProps) {
  const [formData, setFormDataState] =
    useState<SetEditorFormData>(defaultFormData);
  const [savePayload, setSavePayload] = useState<SetEditorContent | null>(null);

  const setFormData = useCallback((data: Partial<SetEditorFormData>) => {
    setFormDataState((prev) => ({ ...prev, ...data }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormDataState(defaultFormData);
    setSavePayload(null);
  }, []);

  const value = useMemo(
    () => ({
      formData,
      savePayload,
      setFormData,
      setSavePayload,
      resetFormData,
    }),
    [formData, savePayload, setFormData, resetFormData],
  );

  return (
    <SetEditorContext.Provider value={value}>
      {children}
    </SetEditorContext.Provider>
  );
}
